import type { OnboardingCockpitStatus } from 'src/features/advisor-book-onboarding/constants/cockpit-url-state';
import { blockedReasonLabelFor } from 'src/features/advisor-book-onboarding/constants/cockpit-status-copy';

export type HouseholdOnboardingRibbonVariant = 'warning' | 'info' | 'destructive' | 'success';

export type HouseholdOnboardingPrimaryCta =
  | { readonly kind: 'confirm_accounts' }
  | { readonly kind: 'send_onboarding' }
  | { readonly kind: 'fix_contact_info' }
  | { readonly kind: 'dismiss' };

const RIBBON_VARIANT_BY_STATUS: Readonly<Record<OnboardingCockpitStatus, HouseholdOnboardingRibbonVariant>> = {
  confirm_account: 'warning',
  ready_to_send: 'warning',
  awaiting_client: 'info',
  in_progress: 'info',
  blocked: 'destructive',
  complete: 'success',
};

type SummaryParams = {
  readonly status: OnboardingCockpitStatus;
  readonly blockedReason?: string | null;
  readonly accountsTransferred: number;
  readonly accountsTotal: number;
};

export function householdOnboardingRibbonVariantFor(status: OnboardingCockpitStatus) {
  return RIBBON_VARIANT_BY_STATUS[status];
}

export function householdOnboardingSummaryFor({
  status,
  blockedReason,
  accountsTransferred,
  accountsTotal,
}: SummaryParams) {
  switch (status) {
    case 'confirm_account':
      return 'Accounts need to be confirmed before onboarding can start.';
    case 'ready_to_send':
      return 'Accounts confirmed. Send the onboarding flow to collect info + signatures.';
    case 'awaiting_client':
      return "Onboarding sent. Waiting on the client's signatures to come back.";
    case 'in_progress':
      return `Signatures done. ${accountsTransferred} of ${accountsTotal} accounts transferring.`;
    case 'blocked':
      if (blockedReason === 'missing_client_info') {
        return 'Accounts confirmed, but contact info is incomplete. Fix it before you can send onboarding.';
      }
      if (blockedReason !== null && blockedReason !== undefined && blockedReason !== '') {
        return `${blockedReasonLabelFor(blockedReason)}. Open for details.`;
      }
      return 'Something went wrong. Open for details.';
    case 'complete':
      return 'Onboarding complete.';
  }
}

export function householdOnboardingPrimaryCtaFor({
  status,
  blockedReason,
}: {
  readonly status: OnboardingCockpitStatus;
  readonly blockedReason?: string | null;
}): HouseholdOnboardingPrimaryCta | null {
  if (status === 'confirm_account') {
    return { kind: 'confirm_accounts' };
  }
  if (status === 'ready_to_send') {
    return { kind: 'send_onboarding' };
  }
  if (status === 'blocked' && blockedReason === 'missing_client_info') {
    return { kind: 'fix_contact_info' };
  }
  if (status === 'complete') {
    return { kind: 'dismiss' };
  }
  return null;
}

const DETAIL_STATE_MEANS: Readonly<Record<OnboardingCockpitStatus, string>> = {
  confirm_account:
    'Accounts pulled from the spreadsheet need to be verified against the custodian before onboarding can move forward.',
  ready_to_send:
    "Accounts are confirmed. The onboarding flow is staged. Send it when you're ready and the client will fill in info and sign.",
  awaiting_client: 'The onboarding flow is with the client. Waiting on signatures for IAA and custodial documents.',
  in_progress: 'Signatures are in. Onboarding is in progress on the custodian side.',
  blocked: "Something went wrong in onboarding. Here's what's blocking it and how to resolve.",
  complete: 'Onboarding is done. Accounts are open, signed, and assets have transferred.',
};

export function householdOnboardingDetailStateMeansFor(status: OnboardingCockpitStatus) {
  return DETAIL_STATE_MEANS[status];
}
